import { useLingui } from '@lingui/react/macro';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { DialogTrigger } from '@worldcoo/ui/components/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { diamondGlows, DiamondGrid, PillButton } from '../primitives';
import { ComingSoonDialog } from '../coming-soon-dialog';
import { useSolutions, SOLUTION_KEYS, type Solution, type SolutionCard } from './solutions-data';

const CARD = 'overflow-hidden rounded-3xl';

/** Geometry measured off the hand-off, not guessed. */
function StepCard(props: { card: Extract<SolutionCard, { kind: 'step' }>; number?: string }) {
	return (
		<article className={`bg-sand relative flex min-h-[391px] flex-col p-6 pb-8 ${CARD}`}>
			{props.number && (
				<span className="text-ink/50 absolute top-[27px] right-6 text-sm leading-[1.3] font-medium">
					{props.number}
				</span>
			)}
			{/* `object-contain`, as the drawings do NOT share one canvas; decorative, since the title carries it. */}
			<div className="flex h-[182px] w-[168px] shrink-0 items-center justify-center">
				<img src={props.card.art} alt="" aria-hidden className="h-full w-full object-contain" />
			</div>
			{/* Not `mt-auto`, which lines up the LAST lines, so a long description lifts its title off the row. */}
			<div className="flex flex-col gap-2 pt-6">
				<h3 className="text-2xl leading-[1.3] font-medium">{props.card.title}</h3>
				<p className="text-base leading-[1.5]">{props.card.description}</p>
			</div>
		</article>
	);
}

function MediaCard(props: { card: Extract<SolutionCard, { kind: 'media' }> }) {
	return (
		<div className={`bg-sand min-h-[391px] ${CARD}`}>
			<Image
				src={props.card.src}
				alt={props.card.alt}
				height={782}
				aspectRatio={309 / 391}
				objectFit="cover"
				className="h-full w-full object-cover!"
			/>
		</div>
	);
}

/** The asset IS the whole card; `object-contain` never crops a panel, and `bg-ink` hides the letterbox. */
function ScreenCard(props: { card: Extract<SolutionCard, { kind: 'screen' }> }) {
	const { language } = useTranslation();
	const src = props.card.src[language] ?? props.card.src.en;
	return (
		<div className={`bg-ink relative min-h-[391px] ${CARD}`}>
			<img src={src} alt={props.card.alt} className="absolute inset-0 size-full object-contain" />
		</div>
	);
}

function Panel(props: { solution: Solution }) {
	const { t } = useLingui();
	const cards = props.solution.cards;
	// Numbering two of four slots would invent a sequence.
	const numbered = cards.every((c) => c.kind === 'step');

	return (
		<div className="relative overflow-hidden rounded-3xl bg-white p-6 md:p-12">
			{/* `opacity` 1, as the glow's own 0.4 alpha is the intensity. */}
			<DiamondGrid opacity={1} masks={[diamondGlows({ x: 'calc(100% - 78.5px)', y: '-113.5px' })]} />

			<div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
				{/* The hand-off's two boxes; at 620px the copy ran to four lines where the design has three. */}
				<p
					className={`text-xl leading-[1.3] font-medium md:text-2xl ${props.solution.cta ? 'max-w-[955px]' : 'max-w-[800px]'}`}
				>
					{props.solution.description}
				</p>
				{props.solution.cta && (
					<ComingSoonDialog
						description={t`Our guide to Charity Rounding Up is on its way. Write to us in the meantime and we'll walk you through activating it.`}
					>
						<DialogTrigger asChild>
							<PillButton variant="outline" className="shrink-0">
								{props.solution.cta.label}
							</PillButton>
						</DialogTrigger>
					</ComingSoonDialog>
				)}
			</div>

			<div className="relative mt-8 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
				{cards.map((card, i) => {
					// A panel never repeats a title or an asset.
					if (card.kind === 'step') {
						return (
							<StepCard key={card.title} card={card} number={numbered ? String(i + 1).padStart(2, '0') : undefined} />
						);
					}
					if (card.kind === 'screen') return <ScreenCard key={card.src.en} card={card} />;
					return <MediaCard key={card.src} card={card} />;
				})}
			</div>
		</div>
	);
}

export function SolutionTabs() {
	const { t } = useLingui();
	const solutions = useSolutions();
	const { solution } = useSearch({ from: '/www/solutions' });
	const navigate = useNavigate({ from: '/www/solutions' });

	return (
		<section id="solutions" className="bg-apricot scroll-mt-24">
			<div className="mx-auto max-w-[1440px] px-6 py-16 md:py-20 xl:px-10">
				<h2 className="text-center text-3xl leading-[1.2] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Choose your way of adding up`}
				</h2>

				{/* CONTROLLED, as a footer link followed from this page does not remount it; `replace` spares the back button. */}
				<Tabs
					value={solution ?? SOLUTION_KEYS[0]}
					onValueChange={(value) => {
						void navigate({ search: { solution: value as (typeof SOLUTION_KEYS)[number] }, replace: true });
					}}
					className="mt-10 gap-2 md:mt-12"
				>
					<TabsList
						variant="line"
						className="hide-scrollbar mx-auto max-w-full flex-nowrap justify-start gap-2 overflow-x-auto rounded-none border-0 bg-transparent p-0 group-data-horizontal/tabs:h-auto md:justify-center"
					>
						{solutions.map((s) => (
							<TabsTrigger
								key={s.key}
								value={s.key}
								// The fill, prefixes and order are load-bearing: docs/notes/portals/solution-tabs.md
								className="text-ink group-data-[variant=line]/tabs-list:data-active:bg-ink group-data-[variant=line]/tabs-list:data-active:hover:bg-ink hover:text-ink h-14 flex-none rounded-2xl px-6 text-base font-medium group-data-[variant=line]/tabs-list:bg-[#FBE8D7] after:hidden group-data-[variant=line]/tabs-list:hover:bg-white/70 data-active:text-white data-active:hover:text-white"
							>
								{s.tab}
							</TabsTrigger>
						))}
					</TabsList>

					{solutions.map((s) => (
						<TabsContent key={s.key} value={s.key}>
							<Panel solution={s} />
						</TabsContent>
					))}
				</Tabs>
			</div>
		</section>
	);
}
